<template>
  <div class="deal-tags">
    <!-- Assigned tags as colored chips -->
    <span
      v-for="tag in assignedTags"
      :key="tag.id"
      class="deal-tags__chip"
      :style="chipStyle(tag.color)"
    >
      <span class="deal-tags__chip-dot" :style="{ backgroundColor: tag.color }"></span>
      <span class="deal-tags__chip-text">{{ tag.name }}</span>
      <button
        class="deal-tags__chip-remove"
        :disabled="saving"
        @click.stop="removeTag(tag.id)"
        :aria-label="$t('common.remove')"
      >
        <BaseIcon name="xmark" size="11" />
      </button>
    </span>

    <!-- Add trigger -->
    <button ref="triggerRef" class="deal-tags__add" :disabled="saving" type="button">
      <BaseIcon name="plus" size="12" />
      {{ assignedTags.length ? $t('deals.tags.add') : $t('deals.tags.add_first') }}
    </button>

    <!-- Dropdown (rendered into Tippy) -->
    <div ref="dropdownRef" class="deal-tags__panel">
      <div class="deal-tags__search" v-if="tagStore.tags.length">
        <BaseIcon name="search" size="14" />
        <input v-model="query" :placeholder="$t('deals.tags.search_placeholder')" @click.stop />
      </div>

      <div class="deal-tags__list">
        <button
          v-for="tag in filteredTags"
          :key="tag.id"
          type="button"
          class="deal-tags__option"
          :class="{ 'deal-tags__option--active': isAssigned(tag.id) }"
          @click.stop="toggleTag(tag.id)"
        >
          <span class="deal-tags__option-dot" :style="{ backgroundColor: tag.color }"></span>
          <span class="deal-tags__option-label">{{ tag.name }}</span>
          <BaseIcon v-if="isAssigned(tag.id)" name="check" size="14" />
        </button>

        <div v-if="!filteredTags.length && !canCreateFromQuery" class="deal-tags__empty">
          {{ $t('deals.tags.no_tags') }}
        </div>
      </div>

      <!-- Create section -->
      <div class="deal-tags__create">
        <input
          v-model="newTagName"
          class="deal-tags__create-input"
          :placeholder="$t('deals.tags.create_placeholder')"
          @click.stop
          @keydown.enter.prevent="createAndAssign"
        />
        <div class="deal-tags__swatches">
          <button
            v-for="color in palette"
            :key="color"
            type="button"
            class="deal-tags__swatch"
            :class="{ 'deal-tags__swatch--active': newTagColor === color }"
            :style="{ backgroundColor: color }"
            @click.stop="newTagColor = color"
          ></button>
        </div>
        <Btn
          variant="primary"
          size="xs"
          :loading="creating"
          :disabled="!newTagName.trim()"
          @click="createAndAssign"
        >
          <BaseIcon name="plus" size="12" />
          {{ $t('deals.tags.create') }}
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
  import tippy from 'tippy.js';
  import { useDealStore } from '@/store/dealStore';
  import { useTagStore } from '@/store/tagStore';
  import { useToastStore } from '@/store/toastStore';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  const PALETTE = [
    '#64748b',
    '#3b82f6',
    '#6366f1',
    '#8b5cf6',
    '#ec4899',
    '#ef4444',
    '#f97316',
    '#f59e0b',
    '#10b981',
    '#14b8a6',
  ];

  export default {
    name: 'DealTags',

    components: { Btn, BaseIcon },

    props: {
      dealId: { type: String, required: true },
      deal: { type: Object, required: true },
    },

    emits: ['deal-updated'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const dealStore = useDealStore();
      const tagStore = useTagStore();
      const toastStore = useToastStore();

      const triggerRef = ref(null);
      const dropdownRef = ref(null);
      let tippyInstance = null;

      const saving = ref(false);
      const creating = ref(false);
      const query = ref('');
      const newTagName = ref('');
      const newTagColor = ref(PALETTE[1]);

      const assignedTags = computed(() => {
        if (Array.isArray(props.deal?.tags) && props.deal.tags.length) {
          return props.deal.tags;
        }
        const ids = props.deal?.tag_ids || [];
        return ids.map((id) => tagStore.tagById(id)).filter(Boolean);
      });

      const assignedIds = computed(() => assignedTags.value.map((t) => t.id));

      const filteredTags = computed(() => {
        const q = query.value.trim().toLowerCase();
        if (!q) return tagStore.tags;
        return tagStore.tags.filter((tag) => tag.name.toLowerCase().includes(q));
      });

      const canCreateFromQuery = computed(() => !!newTagName.value.trim());

      const isAssigned = (id) => assignedIds.value.includes(id);

      const chipStyle = (color) => {
        const c = color || '#64748b';
        return {
          backgroundColor: `${c}15`,
          color: c,
          borderColor: `${c}40`,
        };
      };

      const persist = async (tagIds) => {
        saving.value = true;
        try {
          await dealStore.updateDeal(props.dealId, { tag_ids: tagIds });
          emit('deal-updated', dealStore.currentDeal);
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.tags.update_failed') });
        } finally {
          saving.value = false;
        }
      };

      const toggleTag = (id) => {
        const next = isAssigned(id)
          ? assignedIds.value.filter((tagId) => tagId !== id)
          : [...assignedIds.value, id];
        persist(next);
      };

      const removeTag = (id) => {
        persist(assignedIds.value.filter((tagId) => tagId !== id));
      };

      const createAndAssign = async () => {
        const name = newTagName.value.trim();
        if (!name) return;

        creating.value = true;
        try {
          const tag = await tagStore.createTag({ name, color: newTagColor.value });
          await persist([...assignedIds.value, tag.id]);
          newTagName.value = '';
          query.value = '';
          newTagColor.value = PALETTE[1];
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.tags.create_failed') });
        } finally {
          creating.value = false;
        }
      };

      onMounted(async () => {
        try {
          await tagStore.fetchTags();
        } catch (error) {
          // Non-critical: dropdown will just allow creating new tags
        }

        await nextTick();
        if (triggerRef.value && dropdownRef.value) {
          tippyInstance = tippy(triggerRef.value, {
            content: dropdownRef.value,
            interactive: true,
            trigger: 'click',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
          });
        }
      });

      onBeforeUnmount(() => {
        tippyInstance?.destroy();
        tippyInstance = null;
      });

      return {
        tagStore,
        triggerRef,
        dropdownRef,
        saving,
        creating,
        query,
        newTagName,
        newTagColor,
        palette: PALETTE,
        assignedTags,
        filteredTags,
        canCreateFromQuery,
        isAssigned,
        chipStyle,
        toggleTag,
        removeTag,
        createAndAssign,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.375rem;

    &__chip {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.1875rem 0.5rem;
      border: 1px solid;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1;
      max-width: 200px;
    }

    &__chip-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__chip-text {
      @include ellipsis(1);
    }

    &__chip-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      opacity: 0.6;
      border-radius: 2px;
      transition: opacity 0.15s ease;

      @include hover {
        opacity: 1;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
      }
    }

    &__add {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.1875rem 0.5rem;
      background: var(--slate-50);
      border: 1px dashed var(--slate-300);
      border-radius: 0.375rem;
      color: var(--slate-600);
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        color 0.15s ease,
        background-color 0.15s ease;

      @include hover {
        border-color: var(--brand-300);
        color: var(--brand-600);
        background: var(--brand-50);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    &__panel {
      display: none;
      width: 260px;
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      border-bottom: 1px solid var(--slate-200);
      color: var(--slate-400);

      input {
        flex: 1;
        border: none;
        outline: none;
        font-size: 0.8125rem;
        color: var(--slate-700);
        background: none;
      }
    }

    &__list {
      max-height: 220px;
      overflow-y: auto;
      padding: 0.25rem;
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.5rem;
      background: none;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
      font-size: 0.8125rem;
      color: var(--slate-700);
      text-align: left;
      transition: background-color 0.15s ease;

      @include hover {
        background: var(--slate-50);
      }

      &--active {
        color: var(--slate-900);
        font-weight: 500;
      }
    }

    &__option-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    &__option-label {
      flex: 1;
      @include ellipsis(1);
    }

    &__empty {
      padding: 0.75rem;
      text-align: center;
      color: var(--slate-500);
      font-size: 0.8125rem;
      font-style: italic;
    }

    &__create {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border-top: 1px solid var(--slate-200);
      background: var(--slate-50);
    }

    &__create-input {
      width: 100%;
      padding: 0.4375rem 0.5rem;
      border: 1px solid var(--slate-300);
      border-radius: 0.375rem;
      font-size: 0.8125rem;
      color: var(--slate-700);

      &:focus {
        outline: none;
        border-color: var(--brand-400);
        box-shadow: 0 0 0 2px var(--brand-100);
      }
    }

    &__swatches {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
    }

    &__swatch {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid transparent;
      cursor: pointer;
      padding: 0;
      transition: transform 0.15s ease;

      @include hover {
        transform: scale(1.15);
      }

      &--active {
        border-color: var(--slate-900);
      }
    }
  }
</style>

<style lang="scss">
  .tippy-box[data-theme~='dropdown'] .deal-tags__panel {
    display: block;
  }
</style>
